import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useLazyQuery } from '@apollo/client';
import { BaseButton, FollowButton } from '@bnnvara/buttons';
import { useAccount, useLogin } from '@bnnvara/accounts';
import { useRouter } from 'next/router';

import { Heading } from '@bnnvara/typography';
import { Modal } from '@bnnvara/modal';
import { BaseForm } from '@bnnvara/forms/src/new_components/BaseForm/BaseForm';
import { Checkbox } from '@bnnvara/forms/src/new_components/inputs/Checkbox/Checkbox';
import { trackAllAnalytics } from '@bnnvara/analytics/src/track';
import { Loading } from '@bnnvara/icons/src/components/UserInterfaceIcons/Loading';
import useAnalyticProperties from '@/hooks/useAnalyticProperties';

import { EditorialNotificationSettings } from '@/components/Profile/NotificationSettings/EditorialNotificationSettings';
import FetchFollowedThemes from '@/api/queries/me/theme.gql';
import { FetchNotificationSettings } from '@/api/queries/me/notifications.gql';
import { useFingerprint } from '@/hooks/fingerprint';
import { utmParams } from '@/helpers/utmParams';
import { stripQueryParams } from '@/helpers/stripQueryParams';

import toggleFollowTheme from '@/api/mutations/themeFollow.gql';
import * as S from './styled';

const FollowThemes = ({ theme }) => {
  const fingerprint = useFingerprint();
  const utm = utmParams();
  const router = useRouter();
  const analyticsProperties = useAnalyticProperties();

  const { isLoggedIn, login } = useLogin();
  const { profile, token } = useAccount();
  const [toggleFollow] = useMutation(toggleFollowTheme);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] =
    useState(false);

  const [getNotificationSettings, { data: notificationData }] = useLazyQuery(
    FetchNotificationSettings,
    {
      variables: {
        UID: profile.UID,
      },
      skip: !profile.UID,
      fetchPolicy: 'no-cache',
    }
  );

  const [getFollowedThemes, { data: followedThemes, loading }] = useLazyQuery(
    FetchFollowedThemes,
    {
      variables: { userId: profile.UID },
      skip: !profile.UID,
      fetchPolicy: 'no-cache',
    }
  );

  const handleOnRequestClose = useCallback(() => {
    setIsModalOpen(false);
    setShowNotificationSettings(false);
  }, []);

  const openLogin = (e) => {
    e.preventDefault();
    trackAllAnalytics(`klik_subthema_login`, {
      ...analyticsProperties,
      category: 'Header',
    });

    login();
  };

  const handleClick = (e) => {
    return isLoggedIn ? setIsModalOpen(true) : openLogin(e);
  };

  const followedThemeIds = useMemo(
    () => followedThemes?.me?.followedThemes?.map((item) => item.id),
    [followedThemes?.me?.followedThemes]
  );

  const handleToggleFollow = useCallback(
    (value, subtheme) => {
      const trackingLabel = `${value === false ? 'ont' : ''}volgen`;

      trackAllAnalytics(`klik_${trackingLabel}_subthema-${subtheme.name}`, {
        ...analyticsProperties,
        category: 'Header',
      });

      toggleFollow({
        context: {
          headers: {
            'x-jwt-token': token,
          },
        },
        variables: {
          themeId: subtheme.id.toString(),
          isFollowing: value,
          fingerprint,
          utm,
          slug: stripQueryParams(router.asPath),
          brandId:
            process.env.NEXT_PUBLIC_THEME_HARDCODED_BRAND ||
            'd967f9ef-bc31-41d1-94f8-538cc082148a',
        },
      });
    },
    [fingerprint, router.asPath, toggleFollow, token, utm]
  );

  const hasNotifyEmailChecked = useMemo(
    () =>
      notificationData?.me?.notificationSettings.editorialNotificationPerEmail,
    [notificationData?.me?.notificationSettings.editorialNotificationPerEmail]
  );

  const handleButtonSubmit = useCallback(
    (type) => {
      if (type === 'close') {
        setIsModalOpen(false);
        return;
      }

      if (!hasNotifyEmailChecked) {
        setShowNotificationSettings(true);
      } else {
        setIsModalOpen(false);
      }
    },
    [hasNotifyEmailChecked]
  );

  /** Check if there is a single subtheme that is followed */
  const hasAFollowedSubTheme = useMemo(
    () =>
      theme?.subThemes
        .map((subtheme) => followedThemeIds?.includes(subtheme.id))
        .some((item) => typeof item === 'boolean' && item === true),
    [followedThemeIds, theme.subThemes]
  );

  const themeModalContent = useMemo(
    () => (
      <>
        <Heading variant="four" element="h2">
          {theme.name} volgen
        </Heading>
        <S.Subscription>
          <p>Selecteer hieronder waarover je op de hoogte wilt blijven:</p>
          <p>(Je keuze wordt na selecteren automatisch opgeslagen)</p>
        </S.Subscription>
        {!loading &&
          theme.subThemes.map((subtheme, key) => (
            <BaseForm id="follow-themes-form">
              <S.CheckboxGroup>
                <Checkbox
                  name={subtheme.name}
                  label={subtheme.name}
                  className="follow-themes-checkbox"
                  data-testid={`follow-themes-checkbox-${key}`}
                  value={followedThemeIds?.includes(subtheme.id)}
                  onChange={(event) => handleToggleFollow(event, subtheme)}
                />
              </S.CheckboxGroup>
            </BaseForm>
          ))}
        {loading && <Loading />}
      </>
    ),
    [followedThemeIds, handleToggleFollow, loading, theme.name, theme.subThemes]
  );

  /** Fire get followed themes query when modal closes or opens so the checkboxes keeps updated */
  useEffect(() => {
    if (!isModalOpen && isLoggedIn) {
      getFollowedThemes();
      getNotificationSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) setShowNotificationSettings(false);
  }, [isModalOpen]);

  return (
    !theme.parent && (
      <>
        <S.FollowButtonContainer>
          <FollowButton
            onClick={handleClick}
            isFollowedByUser={hasAFollowedSubTheme}
            followText="Volgen"
            unfollowText="Volgend"
            data-testid="follow-button"
            brandThemeName={theme.name}
            className="follow-button"
          />
        </S.FollowButtonContainer>

        <Modal
          onRequestClose={handleOnRequestClose}
          className="modal"
          isOpen={isModalOpen}
        >
          <input
            className="clickToClose"
            onClick={() => setIsModalOpen(false)}
            type="hidden"
          />
          <S.InnerModal>
            {showNotificationSettings ? (
              <S.NotificationContent>
                <EditorialNotificationSettings UID={profile.UID} inModal />
              </S.NotificationContent>
            ) : (
              themeModalContent
            )}
            <S.ButtonGroup>
              {hasNotifyEmailChecked || showNotificationSettings ? (
                <BaseButton
                  rel="alternate"
                  onClick={() => handleButtonSubmit('close')}
                  className="cancelButton"
                >
                  Bewaren
                </BaseButton>
              ) : (
                <>
                  <BaseButton
                    rel="alternate"
                    onClick={() => handleButtonSubmit('continue')}
                    className="submitButton"
                  >
                    Verder
                  </BaseButton>
                </>
              )}
            </S.ButtonGroup>
          </S.InnerModal>
        </Modal>
      </>
    )
  );
};

FollowThemes.propTypes = {
  theme: PropTypes.shape().isRequired,
};

export default FollowThemes;
